import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import type { IcpItem, IcpRanked } from '@nest-recruit/shared';
import { Popover, usePopover } from '../../../../components/Popover';
import { Skeleton } from '../../../../components/Skeleton';
import { errorMessage } from '../../../../lib/api-client';
import { useJevPerson, useJevReason } from '../../api/jev-api';
import { jevFoundOf } from '../../icp-view';
import type { RankedListRow } from '../../ranking-view';
import { ReasonSummary, notReadLine, plainReasonOf } from '../JevMatch';
import jevStyles from '../jev.module.css';
import { needsALookLine } from './IcpCells';
import styles from '../../../icp/components/icp.module.css';

// The "What Jev found" column: a box per line Jev reads and the strongest finding in words; pressing the cell opens the sheet's
// "What Jev found" summary on its own, beside the cell, so the sidebar stays closed. The sidebar keeps the whole section.

const POPOVER_WIDTH = 440;
/** How close in time a close and a press must be to be the same press: both happen in one pointer-down, a few milliseconds apart. */
const SAME_PRESS_MS = 50;

interface JevFoundCellProps {
  readonly jobId: string;
  readonly row: RankedListRow;
  readonly items: readonly IcpItem[];
  readonly ranked: IcpRanked | undefined;
}

export function JevFoundCell({ jobId, row, items, ranked }: JevFoundCellProps): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  // A press on the cell is "outside" the pop-up, so the pop-up closes on pointer-down; the click that follows must not open it again.
  // The page has already re-drawn closed by the time this button's own pointer-down runs, so `isOpen` can't say it was open: when the pop-up
  // closed a moment before this very press, the press was what closed it, and its click is swallowed.
  const closedAt = useRef(0);
  const swallowClick = useRef(false);
  const closeTracked = (): void => {
    closedAt.current = performance.now();
    close();
  };
  const askId = row.profileReading?.askId ?? null;
  if (!ranked || ranked.state !== 'read' || askId === null) return <span className={styles.none}>{noteOf(ranked, row)}</span>;
  const { lines, best } = jevFoundOf(items, ranked.values);
  return (
    <>
      <button type="button" ref={triggerRef} className={styles.foundButton} aria-haspopup="dialog" aria-expanded={isOpen} aria-label={`What Jev found for ${row.name}`} onPointerDown={() => (swallowClick.current = performance.now() - closedAt.current < SAME_PRESS_MS)} onKeyDown={keepFromRow} onClick={() => (swallowClick.current ? (swallowClick.current = false) : toggle())}>
        <span className={styles.foundRow}>
          <span className={styles.glyphs}>
            {lines.map(({ item, glyph }) => (
              <span key={item.id} className={styles.glyph} data-tone={glyph.tone} title={`${item.text} · ${glyph.label}`} aria-hidden>
                {glyph.text}
              </span>
            ))}
          </span>
          <span className={styles.foundOpen}>Open</span>
        </span>
        <span className={styles.foundWords}>{best ? `Best: ${best.item.text} · ${best.glyph.label}` : 'Nothing shown yet · unknown, not a no'}</span>
      </button>
      <Popover anchor={anchor} onClose={closeTracked} width={POPOVER_WIDTH} label={`What Jev found for ${row.name}`}>
        <FoundSheet jobId={jobId} askId={askId} applicationId={row.applicationId} />
      </Popover>
    </>
  );
}

/** Enter on the list opens the focused row's sheet; on this button it opens the pop-up only. */
function keepFromRow(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
}

function noteOf(ranked: IcpRanked | undefined, row: RankedListRow): string {
  if (ranked?.state === 'unreadable') return needsALookLine(row.cv);
  return ranked?.state === 'not_connected' ? 'Jev isn’t connected' : 'Not read yet';
}

/** Fetched only while the pop-up is open, so a list of 500 people never asks for 500 findings. The pop-up holds the summary and nothing else; the lines, quotes and questions stay in the sheet. */
function FoundSheet({ jobId, askId, applicationId }: { readonly jobId: string; readonly askId: string; readonly applicationId: string }): ReactNode {
  const person = useJevPerson(jobId, askId, applicationId);
  const result = person.data?.applicationId === applicationId ? person.data : undefined;
  const isRead = result?.status === 'read' && result.match !== null;
  const needsReason = isRead && !result.reason;
  // Opening the pop-up is the ask, so Haiku's summary is requested at once for someone below the top 50.
  const asked = useJevReason(jobId, askId, applicationId, needsReason);
  // An older stored summary names a line by its joined text (D40): the pop-up shows the short name.
  const reason = plainReasonOf(result?.reason ?? asked.data?.reason ?? null, result?.ask.parts ?? []);
  if (person.isError) return <p className={styles.none}>{errorMessage(person.error)}</p>;
  if (!result) {
    // Findings have never been on screen: lines shaped like them (CLAUDE.md §5a).
    return (
      <div aria-busy="true" aria-label="Loading what Jev found">
        <Skeleton width="60%" />
        <Skeleton width="90%" />
        <Skeleton width="80%" />
      </div>
    );
  }
  return (
    <section aria-label="What Jev found">
      <div className={jevStyles.sheetHead}>
        <span className={jevStyles.tile} data-size="sm" aria-hidden>
          J
        </span>
        <h3 className={jevStyles.sheetTitle}>What Jev found</h3>
      </div>
      {isRead ? (
        <ReasonSummary reason={reason} isWriting={needsReason && !asked.isError && asked.data === undefined} problem={asked.error ? errorMessage(asked.error) : null} />
      ) : (
        <p className={jevStyles.sheetNote}>{notReadLine(result)}</p>
      )}
    </section>
  );
}
